import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { PharmacCheckbox, PharmacIcon, SpacerDefs, spacerPaddings } from '@pharmac/ui'
import React, {
  PropsWithChildren,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

type PharmacMultiSelectItem = {
  value: string | number
  label: string
}

type SelectedValueProps = SpacerDefs & {
  isOpen?: boolean
  variant?: 'primary' | 'secondary'
  displaySize?: 'small' | 'large'
  hasError?: boolean
}

const parseChildren = (children: unknown | React.ReactNode) => {
  const items: PharmacMultiSelectItem[] = []

  if (children) {
    React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        const props = child.props as any

        const isValidElement = React.isValidElement(child) && child.type === 'option'
        const hasProps = typeof props.children === 'string' && typeof props.value !== 'object'

        if (isValidElement && hasProps) {
          const p = child.props as { value: string | number; children: string }

          items.push({
            value: p.value,
            label: p.children,
          })
        }
      }
    })
  }

  return items
}

const SelectRoot = styled.div`
  position: relative;
  height: var(--input-medium-height);
`

const selectBase = css`
  flex: 1 0 auto;
  position: relative;
  display: flex;
  align-items: center;
  align-self: stretch;
  border: 0;
  box-sizing: border-box;
  outline: none;
  background: none;
  max-width: 100%;
  width: 100%;

  &:disabled {
    background-color: var(--color-accent-2);
    opacity: 0.8;
  }

  &:hover {
    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }
`

const inputVariants = (props: SelectedValueProps) => {
  const variant = props.variant === 'secondary' ? 'secondary' : 'primary'

  return css`
    ${selectBase};

    ${variant === 'secondary'
      ? css`
          border: 1px solid var(--input-secondary-border-color);
          background-color: var(--input-secondary-background-color);
          color: var(--input-secondary-text-color);
        `
      : css`
          border: 1px solid var(--input-border-color);
          background-color: var(--input-background-color);
          color: var(--input-text-color);
        `}

    font-family: var(--input-secondary-font-family);

    &::placeholder {
      color: var(--color-accent-1);
      opacity: 1; /* Firefox */
    }
    &:-ms-input-placeholder {
      /* Internet Explorer 10-11 */
      color: var(--color-accent-4);
    }
    &::-ms-input-placeholder {
      /* Microsoft Edge */
      color: var(--color-accent-4);
    }
  `
}

const largeInputStyles = css`
  border-radius: 25px;

  font-size: var(--input-large-font-size);
  font-weight: var(--input-large-font-weight);
  line-height: var(--input-large-line-height);
  height: var(--input-large-height);
  padding: var(--input-large-padding-vertical) var(--input-large-padding-horizontal);
`

const mediumInputStyles = css`
  border-radius: 20px;
  font-size: var(--input-medium-font-size);
  font-weight: var(--input-medium-font-weight);
  line-height: var(--input-medium-line-height);
  height: var(--input-medium-height);
  padding: var(--input-medium-padding-vertical) var(--input-medium-padding-horizontal);
`

const smallInputStyles = css`
  border-radius: 15px;
  font-size: var(--input-medium-font-size);
  font-weight: var(--input-medium-font-weight);
  line-height: var(--input-medium-line-height);
  height: var(--input-small-height);
  padding: var(--input-medium-padding-vertical) var(--input-medium-padding-horizontal);
`

const openBorders = '20px 20px 0 0'
const closedBorders = '20px'

const inputStyles = (props: SelectedValueProps) => {
  switch (props.displaySize) {
    default:
      return mediumInputStyles
    case 'small':
      return smallInputStyles
    case 'large':
      return largeInputStyles
  }
}

const SelectElement = styled.input<SelectedValueProps>`
  ${inputVariants};
  ${inputStyles};
  ${spacerPaddings};

  transition: var(--transition-cubic-bezier);

  border-radius: ${({ isOpen }) => (isOpen ? openBorders : closedBorders)};

  ${(p) => {
    if (p.isOpen)
      return css`
        border-radius: ${openBorders};
        border: 2px solid var(--color-primary);
        border-bottom: 1px solid var(--input-border-color);
      `

    return css`
      border-radius: ${closedBorders};
      border: ${p.hasError
        ? '1px solid var(--color-danger)'
        : '1px solid var(--input-border-color)'};
    `
  }}

  cursor: pointer;

  > div {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
  }
`

const SelectElementIcon = styled.div<{ isOpen?: boolean }>`
  margin-left: auto;

  position: absolute;

  top: calc(50% - 8px);
  right: var(--input-medium-padding-horizontal);

  svg {
    transform: rotate(${({ isOpen }) => (isOpen ? '180deg' : '0deg')});
    transition: var(--transition-cubic-bezier);
  }
`

const SelectDropdown = styled.div<SelectedValueProps>`
  position: absolute;

  display: flex;
  max-height: ${({ isOpen }) => (isOpen ? '20vh' : '0px')};
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-color: var(--color-primary) var(--color-accent-1);
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  z-index: 99;

  width: 100%;

  color: var(--input-text-color);
  font-family: var(--input-font-family);
  border: ${({ isOpen }) => (isOpen ? '2px solid var(--color-primary)' : '0px solid transparent')};
  transition: var(--transition-cubic-bezier);

  border-top: none;
  background-color: var(--input-background-color);

  border-radius: 0 0 18px 18px;
`

const SelectOption = styled.div<{ isChecked?: boolean }>`
  width: 100%;
  color: var(--input-text-color);

  font-family: var(--input-font-family);
  font-weight: var(--input-medium-font-weight);

  padding-left: var(--input-medium-padding-horizontal);

  transition: var(--transition-cubic-bezier);

  background-color: ${({ isChecked }) =>
    isChecked ? 'var(--color-background-light)' : 'var(--color-white)'};

  label {
    font-weight: var(--input-medium-font-weight);

    padding: var(--spacing-small) var(--input-medium-padding-horizontal);
  }

  :hover {
    cursor: pointer;
    background-color: var(--color-background-light);
    color: var(--color-black);
  }

  :last-of-type {
    border-radius: 0 0 18px 18px;
  }

  :not(:last-of-type) {
    border-bottom: 1px solid var(--color-accent-4);
  }
`

type PharmacMultiSelectProps = PropsWithChildren & {
  value?: (string | number)[]
  onChange: (...args: any[]) => void
  error?: boolean
}

export const PharmacMultiSelect = ({
  children,
  onChange,
  value,
  error,
}: PharmacMultiSelectProps) => {
  const selectRef = useRef<HTMLDivElement>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')

  const items = useMemo(() => {
    const items = parseChildren(children)

    return !!search === false
      ? items
      : items.filter((a) => a.label.toLocaleLowerCase().includes(search.toLocaleLowerCase()))
  }, [children, search])

  const selectedLabels = useMemo(() => {
    if ((items && !value) || !Array.isArray(value)) return []

    return value.map((a) => {
      const item = items.find((b) => b.value === a)

      return item?.label
    })
  }, [value, items])

  const handleCheckbox = (checkboxValue: string | number, e: SyntheticEvent<HTMLInputElement>) => {
    const currValue = Array.isArray(value) ? value : []

    const t = e.currentTarget

    return t.checked
      ? currValue.concat(checkboxValue)
      : currValue.filter((a) => a !== checkboxValue)
  }

  useEffect(() => {
    const closeIfClickOutside = (e: MouseEvent) => {
      if (selectRef && selectRef.current) {
        if (!selectRef.current.contains(e.target as Node)) {
          setIsOpen(false)
        }
      }
    }

    if (selectRef && selectRef.current && isOpen) {
      window.addEventListener('click', closeIfClickOutside)
    } else {
      window.removeEventListener('click', closeIfClickOutside)
    }

    return () => window.removeEventListener('click', closeIfClickOutside)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen && !!search) {
      setIsOpen(true)
    }
  }, [search])

  return (
    <SelectRoot
      role="listbox"
      ref={selectRef}
    >
      <SelectElement
        onClick={() => setIsOpen(!isOpen)}
        isOpen={isOpen}
        value={!isOpen ? selectedLabels.join(', ') : search}
        onChange={(e) => setSearch(e.target.value)}
        hasError={error}
      />
      <SelectElementIcon
        isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <PharmacIcon
          icon="chevronDown"
          size="16px"
        />
      </SelectElementIcon>
      <SelectDropdown isOpen={isOpen}>
        {items.map((item, index) => {
          const isChecked = Array.isArray(value) && value.includes(item.value)

          return (
            <SelectOption
              key={index}
              role="option"
              isChecked={isChecked}
            >
              <PharmacCheckbox
                handleChange={(e) => onChange(handleCheckbox(item.value, e))}
                checked={isChecked}
              >
                {item.label}
              </PharmacCheckbox>
            </SelectOption>
          )
        })}
        {!!search && items.length === 0 && <SelectOption>Ei tuloksia</SelectOption>}
      </SelectDropdown>
    </SelectRoot>
  )
}
